import { api, ApiError } from '../api/client';
import { uploadBlob } from '../api/blobUpload';
import {
  encryptFolderManifest,
  decryptFolderManifest,
  mergeManifestEntries,
  fileBlobRefsFromManifest,
  type ManifestCommitResult,
  type ManifestFileBlobRef,
  type ManifestFileBlobRefs,
  type SharedFolderManifestEntry,
  type SharedFolderManifestPlaintext,
} from './sharedFolderManifest';

function resolveManifestFileBlobRefs(
  spec: ManifestFileBlobRefs | undefined,
  manifest: SharedFolderManifestPlaintext,
): ManifestFileBlobRef[] {
  if (spec === undefined) {
    return fileBlobRefsFromManifest(manifest);
  }

  return typeof spec === 'function' ? spec(manifest) : spec;
}

export async function commitSharedFolderGuestManifest(
  folderToken: string,
  guestInviteId: string,
  folderKey: Uint8Array,
  expectedRevision: number,
  manifest: SharedFolderManifestPlaintext,
  fileBlobRefs?: Array<{ fileId: string; blobId: string }>,
): Promise<{ revision: number; guestUploadFilesUsed?: number; guestUploadBytesUsed?: number }> {
  const encrypted = await encryptFolderManifest(folderKey, manifest);
  const manifestBlobId = await uploadBlob(encrypted);
  const committed = await api.commitSharedFolderGuestManifest(folderToken, guestInviteId, {
    expectedRevision,
    manifestBlobId,
    fileBlobRefs,
  });
  return committed;
}

export async function loadSharedFolderGuestManifest(
  folderToken: string,
  guestInviteId: string,
  folderKey: Uint8Array,
): Promise<{
  manifest: SharedFolderManifestPlaintext;
  revision: number;
  manifestBlobId: string;
}> {
  const meta = await api.getSharedFolderGuestManifestMeta(folderToken, guestInviteId);
  const blob = await api.fetchSharedFolderGuestManifest(folderToken, guestInviteId);
  const manifest = await decryptFolderManifest(folderKey, blob);
  return {
    manifest,
    revision: meta.revision,
    manifestBlobId: meta.manifestBlobId,
  };
}

export async function appendGuestManifestEntries(
  folderToken: string,
  guestInviteId: string,
  folderKey: Uint8Array,
  currentRevision: number,
  currentManifest: SharedFolderManifestPlaintext,
  intendedNewEntries: SharedFolderManifestEntry[],
  fileBlobRefs?: ManifestFileBlobRefs,
): Promise<ManifestCommitResult & { guestUploadFilesUsed?: number; guestUploadBytesUsed?: number }> {
  let revision = currentRevision;
  let baseManifest = currentManifest;
  const maxAttempts = 3;
  let guestUploadFilesUsed: number | undefined;
  let guestUploadBytesUsed: number | undefined;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const { manifest, conflicts } = mergeManifestEntries(baseManifest, intendedNewEntries);
    try {
      const committed = await commitSharedFolderGuestManifest(
        folderToken,
        guestInviteId,
        folderKey,
        revision,
        manifest,
        resolveManifestFileBlobRefs(fileBlobRefs, manifest),
      );
      guestUploadFilesUsed = committed.guestUploadFilesUsed;
      guestUploadBytesUsed = committed.guestUploadBytesUsed;
      return {
        revision: committed.revision,
        manifest,
        conflicts,
        guestUploadFilesUsed,
        guestUploadBytesUsed,
      };
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 409 || attempt === maxAttempts - 1) {
        throw error;
      }

      const latest = await loadSharedFolderGuestManifest(folderToken, guestInviteId, folderKey);
      revision = latest.revision;
      baseManifest = latest.manifest;
    }
  }

  throw new Error('manifest_commit_failed');
}

export async function commitGuestManifestMutation(
  folderToken: string,
  guestInviteId: string,
  folderKey: Uint8Array,
  currentRevision: number,
  currentManifest: SharedFolderManifestPlaintext,
  mutate: (latest: SharedFolderManifestPlaintext) => SharedFolderManifestPlaintext,
  fileBlobRefs?: ManifestFileBlobRefs,
): Promise<ManifestCommitResult & { guestUploadFilesUsed?: number; guestUploadBytesUsed?: number }> {
  let revision = currentRevision;
  let baseManifest = currentManifest;
  const maxAttempts = 3;
  let guestUploadFilesUsed: number | undefined;
  let guestUploadBytesUsed: number | undefined;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const manifest = mutate(baseManifest);
    try {
      const committed = await commitSharedFolderGuestManifest(
        folderToken,
        guestInviteId,
        folderKey,
        revision,
        manifest,
        resolveManifestFileBlobRefs(fileBlobRefs, manifest),
      );
      guestUploadFilesUsed = committed.guestUploadFilesUsed;
      guestUploadBytesUsed = committed.guestUploadBytesUsed;
      return {
        revision: committed.revision,
        manifest,
        conflicts: [],
        guestUploadFilesUsed,
        guestUploadBytesUsed,
      };
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 409 || attempt === maxAttempts - 1) {
        throw error;
      }

      const latest = await loadSharedFolderGuestManifest(folderToken, guestInviteId, folderKey);
      revision = latest.revision;
      baseManifest = latest.manifest;
    }
  }

  throw new Error('manifest_commit_failed');
}
